/**
 * KnowledgeMock — presentational mock of the askFinz Knowledge workspace for
 * /apps/knowledge. Browser chrome + unified-search bar over a grid of
 * integration tiles, in the same card style as the /design "Live routing grid".
 *
 * NOTE ON LOGOS: /shared has no brand logos for these third-party platforms,
 * so each tile uses a consistent monochrome category icon as a stand-in (the
 * integration name carries the recognition). Each entry has an optional `img`
 * field — drop a real SVG/PNG into /shared/integrations/ and set `img` to swap
 * the icon for the actual brand logo without touching layout.
 *
 * Theme-aware; no client hooks (server component).
 */
import type { LucideIcon } from "lucide-react";
import {
  Search,
  Sparkles,
  HardDrive,
  Cloud,
  FolderOpen,
  FileText,
  MessageSquare,
  Users,
  GitBranch,
  ClipboardCheck,
  Activity,
  Building2,
  PenTool,
  Package,
  BarChart3,
  Table2,
  ShieldCheck,
} from "lucide-react";

type Integration = { name: string; icon: LucideIcon; img?: string; connected?: boolean };

// First row (connected:true) shows a live pulse dot at full opacity, mirroring
// the routing grid's "active provider" treatment. The rest sit at opacity-70.
const INTEGRATIONS: Integration[] = [
  { name: "Google Drive",    icon: HardDrive,      connected: true },
  { name: "Notion",          icon: FileText,       connected: true },
  { name: "Slack",           icon: MessageSquare,  connected: true },
  { name: "GitHub",          icon: GitBranch,      connected: true },
  { name: "SharePoint",      icon: FolderOpen },
  { name: "OneDrive",        icon: Cloud },
  { name: "Dropbox",         icon: Package },
  { name: "Microsoft Teams", icon: Users },
  { name: "Jira",            icon: ClipboardCheck },
  { name: "Linear",          icon: Activity },
  { name: "Salesforce",      icon: Building2 },
  { name: "Figma",           icon: PenTool },
  { name: "Power BI",        icon: BarChart3 },
  { name: "Airtable",        icon: Table2 },
  { name: "SQL · CSV",       icon: Table2 },
];

export function KnowledgeMock() {
  return (
    <div className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            knowledge.askfinz.ai · coming soon
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <ShieldCheck className="h-3 w-3" />
            Permission-aware
          </span>
        </div>

        <div className="p-4 sm:p-6">
          {/* Unified search bar */}
          <div className="flex items-center gap-3 rounded-2xl border hairline bg-background/70 px-4 py-3">
            <Search className="h-4 w-4 text-foreground/80" />
            <span className="text-sm text-foreground/80">
              Search across everything you&#39;ve connected…
            </span>
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-md border hairline bg-card px-2 py-1 text-[11px] text-foreground/80">
              <Sparkles className="h-3 w-3" /> One index
            </span>
          </div>

          <p className="mt-5 px-1 text-[10px] font-semibold uppercase tracking-wider text-foreground/65 font-mono">
            Connected sources
          </p>

          {/* Integration grid — same card style as the /design Live routing grid */}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {INTEGRATIONS.map((it) => {
              const Icon = it.icon;
              return (
                <div
                  key={it.name}
                  className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl border bg-card px-4 py-7 transition-all duration-500 ${
                    it.connected ? "hairline-strong opacity-100 shadow-sm" : "hairline opacity-60"
                  }`}
                >
                  {it.connected && (
                    <span className="provider-pulse-dot absolute right-3 top-3" aria-hidden="true" />
                  )}
                  {it.img ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img alt={it.name} className="h-6 w-6 object-contain" src={it.img} />
                  ) : (
                    <Icon className="h-6 w-6 text-foreground/70" aria-hidden="true" />
                  )}
                  <span className="text-xs font-medium text-foreground/70">{it.name}</span>
                </div>
              );
            })}
          </div>

          <p className="mt-4 text-center text-[11px] text-foreground/65 font-mono">
            …and more — 25+ platforms, synced into one permission-aware index.
          </p>
        </div>
      </div>
    </div>
  );
}
